import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatConversation,
  ChatSearchPerson,
  ChatService,
  ChatSocketService,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';
import {
  conversationsSelector,
  presenceViewsSelector,
  userDisplaySelector,
} from '../../state/chat.selectors';
import { resolveChatInitials } from '../../chat-initials.util';
import { ChatPresenceView } from '../../chat-status/chat-status.util';

/** Directory lookups only start once the term is meaningful (same as T-22). */
const PEOPLE_SEARCH_MIN_LENGTH = 2;

/** Server-side cap for the directory slice merged into the picker. */
const PEOPLE_SEARCH_LIMIT = 10;

interface ChatPersonOption {
  id: string;
  name: string;
  image?: string;
  initial: string;
  isSelf: boolean;
}

/**
 * Internal Chat "Nova conversa" dialog (SPEC-2026-07-internal-chat, T-18).
 *
 * People picker over the bootstrap `userDisplay` map, EXTENDED (T-24) with the
 * company directory: typing 2+ characters also queries `GET /api/chat/search`
 * (`data.people`, company-scoped server-side) and merges the result, so a
 * colleague you have never talked to is reachable — the bootstrap map only
 * covers people in already-visible conversations. Resolved names are shared
 * with the other chat surfaces through `mergeUserDisplay`.
 *
 * - 1 person selected → DIRECT: an existing DM (matched by directKey) opens
 *   directly; otherwise a DRAFT opens — DMs only materialize server-side on
 *   the first MESSAGE_SEND `{ directUserId }` (RF-01), never via
 *   CHANNEL_CREATE. Selecting yourself opens the personal-notes self pair.
 * - 2+ people selected → GROUP via CHANNEL_CREATE `{ type: GROUP, name?,
 *   memberIds }` (min 3 members including the creator — matches the backend
 *   rule). The name is an OPTIONAL informal label: the field appears once the
 *   selection turns into a group, and left empty the group is displayed by its
 *   member roster, exactly as before.
 */
@Component({
  templateUrl: './chat-direct-create-dialog.component.html',
  styleUrls: ['./chat-direct-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatDirectCreateDialogComponent extends AbstractComponent implements OnInit {

  people: ChatPersonOption[] = [];
  filteredPeople: ChatPersonOption[] = [];
  filterTerm = '';
  selectedIds = new Set<string>();
  /** Optional group label — only read when 2+ people are selected. */
  groupName = '';
  submitting = false;
  loadingDirectory = false;

  private conversations: ChatConversation[] = [];
  /** People resolved through the directory search, merged into `people`. */
  private directoryPeople: ChatUserDisplayMap = {};
  /** userId → presence view (dot class) for the avatar tiles. */
  private presenceViews: Record<string, ChatPresenceView> = {};
  private readonly terms = new Subject<string>();

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatDirectCreateDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    private readonly chatService: ChatService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(userDisplaySelector),
      this.store.select(conversationsSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userDisplay, conversations]) => {
        this.conversations = conversations;
        this.rebuildPeople(userDisplay);
      });

    // Presence + custom status feed the dot on each avatar tile, exactly like
    // the conversation pane.
    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => this.presenceViews = views);

    // Company directory (T-24 · RF-01) — membership-scoped server-side.
    this.terms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < PEOPLE_SEARCH_MIN_LENGTH) {
            this.loadingDirectory = false;
            return of<ChatSearchPerson[]>([]);
          }
          this.loadingDirectory = true;
          return this.chatService.search({ q: term.trim(), limit: PEOPLE_SEARCH_LIMIT })
            .pipe(
              map((envelope) => envelope?.data?.people ?? []),
              catchError(() => of<ChatSearchPerson[]>([])),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((people) => {
        this.loadingDirectory = false;
        if (people.length === 0) {
          return;
        }
        const resolved: ChatUserDisplayMap = {};
        for (const person of people) {
          resolved[person._id] = { name: person.name, image: person.image };
        }
        this.directoryPeople = { ...this.directoryPeople, ...resolved };
        // Share the resolution with every other chat surface (merge only).
        this.store.dispatch(ChatActions.mergeUserDisplay({ userDisplay: resolved }));
      });
  }

  applyFilter(): void {
    this.terms.next(this.filterTerm);

    const term = this.filterTerm.trim().toLowerCase();
    this.filteredPeople = term
      ? this.people.filter((person) => person.name.toLowerCase().includes(term))
      : this.people;
  }

  /**
   * Bootstrap map + directory results, deduplicated by id. Selected people are
   * always kept in the list so a multi-select survives a term change.
   */
  private rebuildPeople(userDisplay: ChatUserDisplayMap): void {
    const currentUserId = this.getIDCurrentUser();
    const merged: ChatUserDisplayMap = { ...userDisplay, ...this.directoryPeople };

    this.people = Object.keys(merged)
      // Removed/blocked/off-company users stay out of the picker (RF-01): the
      // bootstrap flags them `inactive` — their names still label old messages,
      // but a NEW conversation with them makes no sense.
      .filter((id) => !merged[id]?.inactive)
      .map((id) => ({
        id,
        name: merged[id]?.name ?? '',
        image: merged[id]?.image,
        initial: resolveChatInitials(merged[id]?.name || '?'),
        isSelf: id === currentUserId,
      }))
      .filter((person) => !!person.name)
      .sort((a, b) => (a.isSelf === b.isSelf ? a.name.localeCompare(b.name) : (a.isSelf ? 1 : -1)));

    const term = this.filterTerm.trim().toLowerCase();
    this.filteredPeople = term
      ? this.people.filter((person) => person.name.toLowerCase().includes(term) || this.selectedIds.has(person.id))
      : this.people;
  }

  toggle(person: ChatPersonOption): void {
    if (this.selectedIds.has(person.id)) {
      this.selectedIds.delete(person.id);
    } else {
      this.selectedIds.add(person.id);
    }
  }

  /** Dot class for a person's avatar tile — same semantics as everywhere else. */
  dotFor(person: ChatPersonOption): string {
    return this.presenceViews[person.id]?.dotClass ?? 'bg-secondary';
  }

  isSelected(person: ChatPersonOption): boolean {
    return this.selectedIds.has(person.id);
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  close(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.submitting || this.selectedCount === 0) return;

    const selected = Array.from(this.selectedIds);

    if (selected.length === 1) {
      this.openDirect(selected[0]);
      return;
    }

    this.createGroup(selected);
  }

  trackPerson(_index: number, person: ChatPersonOption): string {
    return person.id;
  }

  private openDirect(userId: string): void {
    const directKey = [this.getIDCurrentUser(), userId].sort().join(':');
    const existing = this.conversations.find((conversation) =>
      conversation.type === ChatChannelTypeEnum.DIRECT && conversation.directKey === directKey);

    if (existing) {
      this.store.dispatch(ChatActions.openConversation({ channelId: existing._id }));
    } else {
      this.store.dispatch(ChatActions.openDirectDraft({ userId, directKey }));
    }
    this.dialogRef.close(true);
  }

  private createGroup(memberIds: string[]): void {
    this.submitting = true;
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.CHANNEL_CREATE, {
      type: ChatChannelTypeEnum.GROUP,
      // Optional label — omitted entirely when blank, so the backend keeps the
      // roster-labeled behavior.
      name: this.groupName.trim() || undefined,
      memberIds,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // The created group opens via the `created` fan-out (bridge) and the
          // sidebar refreshes via the bootstrap effect.
          this.store.dispatch(ChatActions.loadBootstrap());
          this.dialogRef.close(true);
        },
        error: (ack) => {
          this.submitting = false;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
